import type {
  HomeFieldConfig,
  HomeFieldValue,
  HomeSectionValues,
} from "../../type/homePage";
import { toEnKey } from "../../type/homePage";
import ImageUploadField from "./ImageUploadField";

interface SectionFieldsProps {
  fields: HomeFieldConfig[];
  values: HomeSectionValues;
  errors?: Record<string, string>;
  /** Prefix id agar id unik antar item, mis. "item-2-" -> id="field-item-2-title". */
  idPrefix?: string;
  compact?: boolean;
  onChange: (key: string, value: HomeFieldValue) => void;
}

const inputClass = (hasError: boolean) =>
  `w-full rounded-xl border px-4 outline-none ${
    hasError
      ? "border-red-400 focus:border-red-500"
      : "border-gray-300 focus:border-blue-500"
  }`;

/** Satu `<input>`/`<textarea>` mentah, dipakai baik untuk sisi Indonesia maupun English. */
function RawInput({
  field,
  id,
  value,
  hasError,
  onChange,
}: {
  field: HomeFieldConfig;
  id: string;
  value: string;
  hasError: boolean;
  onChange: (value: string) => void;
}) {
  return field.type === "textarea" ? (
    <textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder ?? "Type here"}
      rows={field.rows ?? 4}
      className={`${inputClass(hasError)} resize-none p-4`}
    />
  ) : (
    <input
      id={id}
      type={field.type === "email" ? "email" : "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder ?? "Type here"}
      className={`${inputClass(hasError)} h-12`}
    />
  );
}

function TextField({
  field,
  value,
  valueEn,
  error,
  id,
  onChange,
  onChangeEn,
}: {
  field: HomeFieldConfig;
  value: string;
  valueEn: string;
  error?: string;
  id: string;
  onChange: (value: string) => void;
  onChangeEn: (value: string) => void;
}) {
  const hasError = Boolean(error);

  return (
    <div>
      <label
        htmlFor={`field-${id}`}
        className="mb-2 block text-base font-semibold">
        {field.label}
        {field.required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {field.translatable ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Indonesia
            </p>
            <RawInput
              field={field}
              id={`field-${id}`}
              value={value}
              hasError={hasError}
              onChange={onChange}
            />
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              English
            </p>
            <RawInput
              field={field}
              id={`field-${id}-en`}
              value={valueEn}
              hasError={false}
              onChange={onChangeEn}
            />
          </div>
        </div>
      ) : (
        <RawInput
          field={field}
          id={`field-${id}`}
          value={value}
          hasError={hasError}
          onChange={onChange}
        />
      )}

      {field.hint && !error && (
        <p className="mt-1 text-sm text-gray-400">{field.hint}</p>
      )}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}

export default function SectionFields({
  fields,
  values,
  errors,
  idPrefix = "",
  compact = false,
  onChange,
}: SectionFieldsProps) {
  const textFields = fields.filter((field) => field.type !== "image");
  const imageFields = fields.filter((field) => field.type === "image");

  if (fields.length === 0) return null;

  const hasImages = imageFields.length > 0;
  const hasTexts = textFields.length > 0;

  // Teks di kiri, gambar di kanan (sama seperti modal Project). Field
  // translatable (ID | EN berdampingan) butuh lebih banyak ruang horizontal,
  // jadi kolom teks tidak dibatasi lebar tetap seperti sebelumnya.
  const gridClass =
    hasImages && hasTexts
      ? compact
        ? "grid grid-cols-1 gap-8 lg:grid-cols-[1fr_240px]"
        : "grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]"
      : "grid grid-cols-1 gap-8";

  return (
    <div className={gridClass}>
      {hasTexts && (
        <div className="space-y-6">
          {textFields.map((field) => {
            const enKey = toEnKey(field.key);

            return (
              <TextField
                key={field.key}
                field={field}
                id={`${idPrefix}${field.key}`}
                value={
                  typeof values[field.key] === "string"
                    ? (values[field.key] as string)
                    : ""
                }
                valueEn={
                  typeof values[enKey] === "string"
                    ? (values[enKey] as string)
                    : ""
                }
                error={errors?.[field.key]}
                onChange={(value) => onChange(field.key, value)}
                onChangeEn={(value) => onChange(enKey, value)}
              />
            );
          })}
        </div>
      )}

      {hasImages && (
        <div className={`space-y-6 ${!hasTexts ? "max-w-[360px]" : ""}`}>
          {imageFields.map((field) => {
            const value = values[field.key];

            return (
              <ImageUploadField
                key={field.key}
                id={`${idPrefix}${field.key}`}
                label={field.label}
                required={field.required}
                hint={field.hint}
                compact={compact}
                error={errors?.[field.key]}
                value={
                  value instanceof File || typeof value === "string"
                    ? value
                    : null
                }
                onChange={(file) => onChange(field.key, file)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
